import React, {
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	Drawer,
	Form,
	Grid,
} from 'antd';
import { useQuery } from 'react-query';
import debounce from 'lodash.debounce';
import {
	Currencies,
	ExpenseTypes,
	LinerCostUnits,
	LinerCostUnitType,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { asyncMap } from '@shared/utils/array';
import normalize from '@shared/utils/normalizeNumber';
import isItemPortCall from '@shared/utils/isItemPortCall';
import { splitActionKey } from '@shared/utils/splitActionKey';
import { GetVoyageExpensesResponse } from '@api/features/invoices/getVoyageExpenses';
import {
	createSupplier,
	createVoyageExpense,
	getSuppliers,
	getVoyageItinerary,
	getVoyages,
	updateVoyageExpense,
} from '@client/lib/api';
import { useNavigationBlock } from '@client/lib/navigationBlock';
import showErrorNotification from '@client/utils/showErrorNotification';
import useFileUpload, { Upload } from '@client/utils/hooks/useFileUpload';
import InvoiceAttachment from '@client/screens/fleet/VoyageDetailsScreen/components/InvoiceAttachment';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import APIError from '@client/utils/APIError';
import getFileFromAttachment from '@client/utils/getFileFromAttachment';
import LoadingIndicator from '@client/components/LoadingIndicator';
import { ItineraryPortCallDto } from '@client/screens/estimates/details/helpers/types';
import { VendorExpenseVisible } from '@client/screens/fleet/VoyageDetailsScreen/components/VendorExpensesCard';
import Modal from '@client/components/Modal/Modal';
import EuExpenseFormItems from '../CreateEuExpenseForm/EuExpenseFormItems';
import EuExpenseVcFormItems from '../CreateEuExpenseForm/EuExpenseVcFormItems';
import styles from './CreateExpenseForm.module.css';
import FormItems from './FormItems';
import VcFormItems from './VcFormItems';

type Props = {
	open: VendorExpenseVisible;
	onClose: () => void;
	initialFiles?: Upload[];
	expenseData?: Partial<Omit<
		GetVoyageExpensesResponse['rows'][number],
		'charterer'
	> & {
		charterer?: number | null;
		allocatedToId?: number | null;
	}> | null;
	documentId?: number;
	voyageId?: number;
	refreshDetails?: () => void;
};

const CreateExpenseForm = ({
	open,
	onClose,
	initialFiles,
	documentId,
	expenseData,
	voyageId,
	refreshDetails,
}: Props) => {
	const [expenseType, setExpenseType] = useState<Values<typeof ExpenseTypes>>('payable');
	const [suppliers] = useFetchedState(getSuppliers);
	const [searchQuery, setSearchQuery] = useState<undefined | string>();
	const disabled = expenseData?.bunkerStemId != null;

	const { data: allVoyages } = useQuery(
		['voyages', searchQuery],
		() =>
			getVoyages({
				search: searchQuery,
				disablePagination: true,
				includeCompleted: true
			}),
		{
			enabled: open !== false || (searchQuery != null && searchQuery !== ''),
			keepPreviousData: true,
			refetchOnWindowFocus: false,
		},
	);

	const voyages = useMemo(() => {
		if (allVoyages == null) {
			return [];
		}

		return allVoyages.rows;
	}, [allVoyages]);

	const onSearch = useMemo(() => {
		return debounce((q) => setSearchQuery(q), 400);
	}, [setSearchQuery]);

	const [loadingAttachments, setLoadingAttachments] = useState(false);
	const [creatingNewSupplier, setCreatingNewSupplier] = useState(false);
	const [portCalls, setPortCalls] = useState<ItineraryPortCallDto[]>([]);
	const [selectedPortCall, setSelectedPortCall] = useState<ItineraryPortCallDto>();
	const [linerCostUnitPriceValues, setLinerCostUnitPriceValues] = useState({
		value: expenseData?.linerCostPricePerUnit,
		currency: expenseData?.currency ?? Currencies.USD,
		exchangeRate: expenseData?.exchangeRate ?? 1,
	});

	const isPortDA = open === 'portDA';
	const isLinerCost = open === 'linerCost';
	const isFuelEuPenalty = open === 'fuelEuPenalty';
	const isEuEts = open === 'euEts';
	const isBasicExpense = open === true;

	useQuery(
		['itinerary', isPortDA, isLinerCost, voyageId],
		() => getVoyageItinerary(voyageId!),
		{
			enabled: voyageId != null && (isPortDA || isLinerCost),
			onSuccess: (data) => {
				const filtered = data.filter((e) => isItemPortCall(e)) as ItineraryPortCallDto[];
				setPortCalls(filtered);
			},
		},
	);

	const linerCostValues = useMemo(() => {
		if (selectedPortCall == null || !isLinerCost) {
			return null;
		}

		let loadDisAction = selectedPortCall.actions
			.find((a) => a.id === expenseData?.portCallId);

		if (loadDisAction == null) {
			const [firstAction] = selectedPortCall.actions.filter(
				(a) => a.event === 'loading' || a.event === 'discharging',
			);
			loadDisAction = firstAction;
		}

		if (loadDisAction == null || loadDisAction.port == null || loadDisAction.Cargo == null) {
			return null;
		}

		const loadDisActionCargoPorts = loadDisAction.Cargo.CargoPorts;

		const { event, port } = loadDisAction;
		const cargoPort = loadDisActionCargoPorts.find((cp) => {
			const { action, portId } = splitActionKey(cp.portAndActionKey);

			return action === event && Number(portId) === port!.id;
		});

		const cargo = loadDisAction.Cargo;

		let unit: LinerCostUnitType | undefined = cargo?.unit;
		let type = cargoPort?.blQuantity != null ? 'BL Quantity' : 'CP Quantity';

		let quantity = cargoPort?.blQuantity ??
				cargoPort?.cpQuantity ??
				0;

		if (cargoPort != null) {
			unit = cargoPort.linerCostUnit ?? cargo.unit;
	
			if (unit === LinerCostUnits.LS) {
				quantity = 1;
				type = 'Lump sum';
			} else if (cargo.secondaryUnit != null && unit === cargo.secondaryUnit) {
				quantity = cargoPort.secondaryBlQuantity ?? cargoPort.secondaryCpQuantity ?? 0;

				type = cargoPort.secondaryBlQuantity != null ?
					'BL Quantity (secondary)' :
					'CP Quantity (secondary)';
			}
		}

		return {
			type,
			quantity,
			action: loadDisAction?.event,
			unit,
		};
	}, [expenseData?.portCallId, isLinerCost, selectedPortCall]);

	const title = useMemo(() => {
		if (open === 'basic' || isBasicExpense) {
			return 'vendor expense';
		}

		if (open === 'portDA') {
			return 'port DA';
		}

		if (open === 'linerCost') {
			return 'liner cost';
		}

		if (open === 'fuelEuPenalty') {
			return 'Fuel EU Penalty';
		}

		if (open === 'euEts') {
			return 'EU ETS';
		}

		return '';
	}, [open, isBasicExpense]);

	const {
		uploaded,
		uploadProps,
		onRemoveUpload,
		onSetUploaded,
		onResetUploaded,
	} = useFileUpload({ multiple: true });

	const [dirty, setDirty] = useState(false);
	const [form] = Form.useForm();

	const {
		useBlocker,
		makeBlockable,
	} = useNavigationBlock();

	const screens = Grid.useBreakpoint();

	useEffect(() => {
		if (initialFiles == null || initialFiles.length === 0) {
			return;
		}

		onSetUploaded(initialFiles);
	}, [onSetUploaded, initialFiles]);

	useEffect(() => {
		if (!open) {
			onResetUploaded();
		}
	}, [onResetUploaded, open]);

	useEffect(() => {
		const pcId = form.getFieldValue('portCallId');

		if (pcId != null) {
			const relevantPortCall = portCalls.find((p) => p.id === pcId);

			if (relevantPortCall != null) {
				setSelectedPortCall(relevantPortCall);
			}
		}
	}, [form, portCalls]);

	const close = () => {
		onClose();
		setDirty(false);
		form.resetFields();
		setSelectedPortCall(undefined);
	};

	const onCloseForm = makeBlockable(close);

	useEffect(() => {
		if (expenseData != null) {
			const editingExpenseType = expenseData.receivable == null ?
				ExpenseTypes.PAYABLE :
				ExpenseTypes.BOTH;

			setExpenseType(editingExpenseType);

			form.setFieldsValue({
				...expenseData,
				type: editingExpenseType,
				amount: {
					value: expenseData.amount,
					currency: expenseData.currency,
					exchangeRate: expenseData.exchangeRate,
				},
				linerCost: {
					value: expenseData.linerCostPricePerUnit,
					currency: expenseData.currency,
					exchangeRate: expenseData.exchangeRate,
				},
				isEuEts,
				isFuelEuPenalty,
			});

			if (expenseData.linerCostPricePerUnit != null) {
				setLinerCostUnitPriceValues({
					value: expenseData.linerCostPricePerUnit,
					currency: expenseData.currency ?? Currencies.USD,
					exchangeRate: expenseData.exchangeRate ?? 1,
				});
			}
		}
	}, [expenseData, form, isEuEts, isFuelEuPenalty]);

	const attachmentUpload = useMemo(() => (
		<div className={styles.attachmentsInner}>
			<InvoiceAttachment
				files={uploaded}
				uploadProps={uploadProps}
				onRemoveFile={onRemoveUpload}
			/>
		</div>
	), [onRemoveUpload, uploadProps, uploaded]);

	useBlocker(dirty);

	const saveVendorExpense = async () => {
		const {
			voyage,
			itemDescription,
			amount,
			linerCost,
			customInvoiceId,
			note,
			supplierName,
			invoiceDate,
			dueDate,
			type,
			portCallId,
			charterer,
			estimated,
			accountCodeOverride,
			isRevenue,
			allocatedToId,
		} = form.getFieldsValue(true);

		let { supplierId } = form.getFieldsValue(true);

		try {
			if (creatingNewSupplier && supplierName != null) {
				const supplier = await createSupplier(supplierName);
				supplierId = supplier.id;
			}

			let amountToUse = amount ? amount.value : 0;
			let currencyToUse = amount ? amount.currency : Currencies.USD;
			let exchangeRateToUse = amount?.exchangeRate ?? null;

			if (isLinerCost) {
				const {
					value,
					currency,
					exchangeRate,
				} = linerCost;

				if (
					value != null &&
						currency != null &&
						exchangeRate != null
				) {
					amountToUse = value * normalize(linerCostValues?.quantity ?? 1);
					currencyToUse = currency;
					exchangeRateToUse = exchangeRate;
				}
			}

			const params = {
				attachments: Array.isArray(uploaded) ? uploaded.map((u) => u.file) : uploaded,
				itemDescription: isEuEts || isFuelEuPenalty ? '' : itemDescription,
				amount: amountToUse,
				currency: currencyToUse,
				exchangeRate: exchangeRateToUse,
				note,
				customInvoiceId,
				voyageId: voyageId ?? voyage,
				documentId,
				supplierId: supplierId == null ? null : supplierId,
				invoiceDate,
				dueDate,
				type,
				isFuelEuPenalty,
				isEuEts,
				portCallId,
				isPortDA,
				isLinerCost,
				charterer,
				estimated,
				accountCodeOverride,
				linerCostPricePerUnit: linerCost?.value,
				linerCostCurrency: linerCost?.currency,
				linerCostExchangeRate: linerCost?.exchangeRate,
				isRevenue,
				allocatedToId,
			};

			if (expenseData != null && expenseData.id != null) {
				await updateVoyageExpense(expenseData.id, params);
				showSuccessNotification(
					'Expense updated',
					'The expense was successfully updated',
				);
			} else {
				if (params.amount < 0 && params.type === ExpenseTypes.BOTH) {
					throw new APIError('You cannot create corresponding receivable from a negative payable', true, 400);
				}

				await createVoyageExpense(params);
				showSuccessNotification(
					'Expense created',
					'The expense was successfully created',
				);
			}

			refreshDetails?.();

			close();
		} catch (e) {
			console.error(e);
			showErrorNotification('Could not save expense', e as Error);
		}
	};

	useEffect(() => {
		const updateDefaultFiles = async () => {
			setLoadingAttachments(true);

			if (expenseData == null || expenseData.attachments?.length === 0) {
				setLoadingAttachments(false);

				return;
			}

			try {
				const getUploadObject = async (attachment: { name: string; id: number; type: string }) => {
					const file: File & {
						uid?: string;
						attachmentId?: number;
						deleteable?: boolean;
					} = await getFileFromAttachment(attachment);

					file.uid = `${file.name}-${file.lastModified}`;
					file.attachmentId = attachment.id;

					return {
						file: file as File & {
							uid: string;
							attachmentId: number;
							deleteable?: boolean;
						},
						url: window.URL.createObjectURL(file),
					};
				};

				const expenseAttachments = expenseData.attachments;

				if (expenseAttachments == null) {
					return;
				}

				const expenseFiles = await asyncMap(
					expenseAttachments,
					async (attachment) => {
						const uploadObj: {
							url?: any;
							deleteable?: boolean;
							file: File & {
								uid?: string;
								attachmentId?: number;
							};
						} = await getUploadObject(attachment);

						uploadObj.deleteable = true;

						return uploadObj as {
							url: any;
							deleteable?: boolean;
							file: File & {
								uid: string;
								attachmentId: number;
							};
						};
					},
				);

				onSetUploaded(expenseFiles);
			} catch (e) {
				showErrorNotification('Could not load attachments', e as Error);
			}

			setLoadingAttachments(false);
		};

		updateDefaultFiles();
	}, [onSetUploaded, expenseData]);

	const handleModalBackgroundClick = (e: React.MouseEvent) => {
		if (e.target === e.currentTarget) {
			onCloseForm();
		}
	};

	return (
		<>
			<Modal
				open={open !== false}
				wrapClassName={styles.rightSideModalWrapper}
				footer={null}
				width="100%"
				mask={false}
			>
				<div className={styles.drawerSpacerContainer}>
					<div className={styles.drawerSpacer} />
					<div
						className={styles.attachmentsDraggerContainer}
						onClick={handleModalBackgroundClick}
						role="presentation"
					>
						{loadingAttachments ? (
							<LoadingIndicator />
						) : (
							!screens.xs && attachmentUpload
						)}
					</div>
				</div>
			</Modal>
			<Drawer
				open={open !== false}
				placement="left"
				width={350}
				title={`${expenseData?.id != null ? 'Update' : 'Create new'} ${title}`}
				onClose={onCloseForm}
				mask={!open}
			>
				<Form
					layout="vertical"
					form={form}
					onValuesChange={(v: {
						type: Values<typeof ExpenseTypes>;
						portCallId: number;
						linerCost: {
							value: number;
							currency: Values<typeof Currencies>;
							exchangeRate: number;
						};
					}) => {
						if (v?.linerCost != null) {
							setLinerCostUnitPriceValues(v.linerCost);
						}

						if (v?.type != null) {
							setExpenseType(v.type);
						}

						if (v.portCallId != null) {
							const pc = portCalls.find((p) => p.id === v.portCallId);
							setSelectedPortCall(pc);
						}
					}}
					onFinish={saveVendorExpense}
					preserve
				>
					{voyageId == null ? (
						<>
							{
								(open === 'fuelEuPenalty' || open === 'euEts') ? (
									<EuExpenseFormItems
										voyages={voyages}
										form={form}
										extra={screens.xs ? attachmentUpload : undefined}
									/>
								) : (
									<FormItems
										onVoyageSearch={onSearch}
										creatingNewSupplier={creatingNewSupplier}
										setCreatingNewSupplier={setCreatingNewSupplier}
										voyages={voyages}
										suppliers={suppliers}
										form={form}
										extra={screens.xs ? attachmentUpload : undefined}
									/>
								)
							}
						</>
					) : (
						<>

							{open === 'fuelEuPenalty' || open === 'euEts' ? (
								<EuExpenseVcFormItems
									voyages={voyages}
									expenseType={expenseType}
									voyageId={voyageId}
									isEditing={expenseData?.id != null}
									extra={screens.xs ? attachmentUpload : undefined}
								/>
							) : (
								<VcFormItems
									disabled={disabled}
									voyages={voyages}
									suppliers={suppliers}
									expenseType={expenseType}
									onCloseForm={onCloseForm}
									voyageId={voyageId}
									expenseSubType={(() => {
										if (typeof open === 'boolean') {
											return 'basic';
										}

										return open;
									})()}
									isBunkerStem={expenseData?.isBunkerStem ?? false}
									isEditing={expenseData?.id != null}
									extra={screens.xs ? attachmentUpload : undefined}
									creatingNewSupplier={creatingNewSupplier}
									setCreatingNewSupplier={setCreatingNewSupplier}
									setSelectedPortCall={setSelectedPortCall}
									portCalls={portCalls}
									linerCostValues={linerCostValues}
									linerCostUnitPriceValues={linerCostUnitPriceValues}
								/>
							)}
						</>

					)}
				</Form>
			</Drawer>
		</>
	);
};

export default CreateExpenseForm;
